// Integration voting / waiting list (VERA-407, ported from Vergence Phase 9.0).
// API client for the vote list + suggest flow + Internal Labs curation queue.

import { createApiCall, statusOf } from './index'

export interface IntegrationRequest {
  id: string
  slug: string
  name: string
  category: string
  categories: string[]
  description: string | null
  logo: string | null
  status: 'pending_review' | 'planned' | 'shipping_soon' | 'shipped' | 'declined'
  voteCount: number
  hasVoted: boolean
  /** 0 = no votes; 1..5 = bottom→top priority tier within the voted subset. */
  priorityTier: number
}

export interface ToggleVoteResponse {
  voted: boolean
  voteCount: number
}

export interface SimilarRequest {
  id: string
  name: string
  slug: string
  status: string
  voteCount: number
}

export interface DuplicateRequest {
  id: string
  name: string
  slug: string
  status: string
}

export interface InternalIntegrationRequest {
  id: string
  slug: string
  name: string
  category: string
  categories: string[]
  description: string | null
  logo: string | null
  url: string | null
  submitterReason: string | null
  status: IntegrationRequest['status']
  submitterEmail: string | null
  createdAt: string
  voteCount: number
}

export type RoadmapFit = 'A' | 'A-' | 'B+' | 'B' | 'B-' | 'C' | 'D'
export type RoadmapRole = 'connect' | 'supply' | 'rival'

/** A row of the internal Roadmap tab (`GET /internal/integration-requests/roadmap`). */
export interface RoadmapIntegration {
  id: string
  slug: string
  name: string
  logo: string | null
  url: string | null
  categories: string[]
  status: IntegrationRequest['status']
  /** Manual rank; 1 = do first. null = not ranked yet. */
  internalPriority: number | null
  fit: RoadmapFit | null
  roles: RoadmapRole[]
  /** Listed on EON's partner network page; null = not checked. */
  eonListed: boolean | null
  veraRefs: string[]
  rationale: string | null
  /** Whether customers can see/vote on it at all. Roadmap seed rows are false. */
  customerVisible: boolean
  source: 'customer' | 'internal'
  voteCount: number
  createdAt: string
}

export interface RoadmapUpdate {
  internalPriority?: number | null
  fit?: RoadmapFit | null
  roles?: RoadmapRole[]
  rationale?: string | null
}

export interface IntegrationAnalyticsSummaryItem {
  slug: string
  name: string
  logo: string | null
  category: string | null
  categories: string[]
  installs: number
  activeInstalls: number
  votes: number
}

export interface IntegrationAnalytics {
  granularity: 'day' | 'week' | 'month'
  /** Period labels aligned index-wise with every bySlug/total array. */
  periods: string[]
  demand: {
    bySlug: Record<string, number[]>
    total: number[]
  }
  installs: {
    bySlug: Record<string, number[]>
    total: number[]
  }
  /** Sorted by installs desc. */
  summary: IntegrationAnalyticsSummaryItem[]
}

export interface IntegrationVoter {
  email: string
  name: string | null
  company: string | null
  votedAt: string
  reason: string | null
}

export const integrationRequestsApi = {
  async list(): Promise<IntegrationRequest[]> {
    const response = await createApiCall({ method: 'GET', url: '/integrations/requests' })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to load integration requests')
    }
    return response.data
  },

  async toggleVote(requestId: string, reason?: string): Promise<ToggleVoteResponse> {
    const response = await createApiCall({
      method: 'POST',
      url: `/integrations/requests/${requestId}/vote`,
      data: { reason },
    })
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(response.data?.message || 'Failed to record vote')
    }
    return response.data
  },

  async suggest(payload: {
    url: string
    reason?: string
    forceSubmit?: boolean
  }): Promise<{ requestId?: string; duplicateOf?: DuplicateRequest; message?: string }> {
    try {
      const response = await createApiCall({
        method: 'POST',
        url: '/integrations/requests',
        data: payload,
      })
      return response.data
    } catch (error: any) {
      // 409 = exact-slug duplicate (Layer-1 dedup); surface duplicateOf for the "vote instead"
      // CTA. Read off the REJECTION: `createApiCall` rejects on every >= 400 (VRG-386), so the
      // old `if (response.status === 409)` line was unreachable and a duplicate suggestion
      // surfaced as a generic error toast instead of offering the vote.
      if (statusOf(error) === 409) {
        return { duplicateOf: error?.response?.data?.duplicateOf }
      }
      throw error
    }
  },

  async similar(query: string): Promise<SimilarRequest[]> {
    if (query.trim().length < 2) return []
    // A typeahead soft-fails to "no suggestions" on ANY failure, deliberately: this fires on
    // every keystroke and a failing endpoint must not throw an error per character into the
    // modal. That intent used to be spelled `if (response.status !== 200) return []`, which
    // stopped running once the client began rejecting (VRG-386), so it IS a catch now. The
    // broad swallow is the point here and is confined to this one suggestion list.
    return createApiCall({
      method: 'GET',
      url: `/integrations/requests/similar?q=${encodeURIComponent(query.trim())}`,
    })
      .then((response) => response.data)
      .catch(() => [])
  },

  // --- Internal Labs curation queue (Vera staff) ---
  async internalList(status: 'pending_review' | 'all' = 'pending_review'): Promise<InternalIntegrationRequest[]> {
    const response = await createApiCall({
      method: 'GET',
      url: `/internal/integration-requests?status=${status}`,
    })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to load suggestions')
    }
    return response.data
  },

  async promote(id: string, categories: string[] = []): Promise<void> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/integration-requests/${id}/promote`,
      data: { categories },
    })
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(response.data?.message || 'Failed to promote')
    }
  },

  async decline(id: string): Promise<void> {
    const response = await createApiCall({ method: 'POST', url: `/internal/integration-requests/${id}/decline` })
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(response.data?.message || 'Failed to decline')
    }
  },

  async analytics(params: { days?: number; granularity?: 'day' | 'week' | 'month' } = {}): Promise<IntegrationAnalytics> {
    const qs = new URLSearchParams()
    if (params.days != null) qs.set('days', String(params.days))
    if (params.granularity) qs.set('granularity', params.granularity)
    const url = `/internal/integration-requests/analytics${qs.toString() ? `?${qs.toString()}` : ''}`
    const response = await createApiCall({ method: 'GET', url })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to load integration analytics')
    }
    return response.data
  },

  async roadmap(): Promise<RoadmapIntegration[]> {
    const response = await createApiCall({ method: 'GET', url: '/internal/integration-requests/roadmap' })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to load the roadmap')
    }
    return response.data
  },

  async updateRoadmap(id: string, patch: RoadmapUpdate): Promise<RoadmapIntegration> {
    const response = await createApiCall({
      method: 'PATCH',
      url: `/internal/integration-requests/${id}/roadmap`,
      data: patch,
    })
    return response.data
  },

  async voters(id: string): Promise<IntegrationVoter[]> {
    const response = await createApiCall({ method: 'GET', url: `/internal/integration-requests/${id}/voters` })
    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to load voters')
    }
    return response.data
  },
}
